import { supabase } from "@/integrations/supabase/client";
import { Candidate } from "@/types/candidate";
import { normalizeSkill } from "@/utils/skillNormalization";
import {
	DEFAULT_PEOPLE_SEARCH_SORT,
	KeywordCriteria,
	PeopleSearchCriteria,
	PeopleSearchSort,
	isCategoryActive,
} from "@/types/peopleSearch";

export const PEOPLE_SEARCH_PAGE_SIZE = 25;

// Chunk size for .in() filters to stay well under PostgREST URL length limits
const IN_CHUNK_SIZE = 200;
// Batch size for id-only universe scans
const UNIVERSE_BATCH_SIZE = 1000;

const CANDIDATE_COLUMNS = 'id, name, email, phone, function, skills, match_score, status, location, default_resume, manager, created_at';

interface UniverseRow {
	id: string;
	created_at: string;
	name: string | null;
}

export interface ResolvedPeopleSearch {
	mode: 'direct' | 'idset';
	count: number;
	// mode 'idset' only: final matching candidates (id + sort fields), created_at desc
	rows?: UniverseRow[];
}

// Same helper pattern as AddToListDialog.tsx / queryBuilder.ts
async function getCurrentOrganization(): Promise<string> {
	const { data: { user } } = await supabase.auth.getUser();
	if (!user) throw new Error('User not authenticated');

	const { data: userOrgs } = await supabase
		.from('user_organization')
		.select('organization_uuid')
		.eq('user_uuid', user.id)
		.single();

	if (!userOrgs) throw new Error('User not associated with any organization');
	return userOrgs.organization_uuid;
}

function chunk<T>(items: T[], size: number): T[][] {
	const chunks: T[][] = [];
	for (let i = 0; i < items.length; i += size)
		chunks.push(items.slice(i, i + size));
	return chunks;
}

function union(sets: Set<string>[]): Set<string> {
	const result = new Set<string>();
	for (const set of sets)
		for (const id of set)
			result.add(id);
	return result;
}

function intersect(a: Set<string>, b: Set<string>): Set<string> {
	const [small, large] = a.size <= b.size ? [a, b] : [b, a];
	const result = new Set<string>();
	for (const id of small)
		if (large.has(id))
			result.add(id);
	return result;
}

// Strip characters that would break a PostgREST .or() filter string
function sanitizePattern(value: string): string {
	return value.replace(/[,()]/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// ID-set resolvers — each returns candidate ids matching one category.
// Org scoping of junction tables is enforced via the universe intersection in
// resolvePeopleSearch, plus candidates!inner joins where cheap.
// ---------------------------------------------------------------------------

async function resolveRoleIds(role: PeopleSearchCriteria['role'], organizationUuid: string): Promise<Set<string>> {
	const sets: Set<string>[] = [];

	for (const title of role.titles) {
		// Current title lives on candidates.function
		if (role.timeframe !== 'former') {
			const { data, error } = await supabase
				.from('candidates')
				.select('id')
				.eq('organization_uuid', organizationUuid)
				.ilike('function', `%${title}%`);
			if (error) throw error;
			sets.push(new Set((data || []).map(row => row.id)));
		}

		// Employment history lives on candidate_experiences (end_date NULL = current)
		// @ts-ignore - table exists but types not yet regenerated
		let query = supabase
			.from('candidate_experiences')
			.select('candidate_id')
			.eq('organization_uuid', organizationUuid)
			.ilike('raw_text', `%${title}%`);
		if (role.timeframe === 'current')
			query = query.is('end_date', null);
		else if (role.timeframe === 'former')
			query = query.not('end_date', 'is', null);
		const { data, error } = await query;
		if (error) throw error;
		// @ts-ignore - untyped rows from stale generated types
		sets.push(new Set((data || []).map(row => row.candidate_id)));
	}

	return union(sets);
}

async function resolveCompanyIds(company: PeopleSearchCriteria['company'], organizationUuid: string): Promise<Set<string>> {
	const sets: Set<string>[] = [];

	for (const name of company.names) {
		// Employer is free text inside candidate_experiences.metadata
		// @ts-ignore - table exists but types not yet regenerated
		let query = supabase
			.from('candidate_experiences')
			.select('candidate_id')
			.eq('organization_uuid', organizationUuid)
			.ilike('metadata->>company', `%${name}%`);
		if (company.timeframe === 'current')
			query = query.is('end_date', null);
		else if (company.timeframe === 'former')
			query = query.not('end_date', 'is', null);
		const { data, error } = await query;
		if (error) throw error;
		// @ts-ignore - untyped rows from stale generated types
		sets.push(new Set((data || []).map(row => row.candidate_id)));
	}

	return union(sets);
}

async function resolveSingleSkillIds(skill: string, organizationUuid: string): Promise<Set<string>> {
	const sets: Set<string>[] = [];

	// Normalized path: org_skills name match -> candidate_skills
	// @ts-ignore - table exists but types not yet regenerated
	const { data: normalizedMatches, error: normalizedError } = await supabase
		.from('org_skills')
		.select('normalized_skill_id')
		.eq('organization_uuid', organizationUuid)
		.gt('candidate_link_count', 0)
		.ilike('name', `%${skill}%`)
		.limit(IN_CHUNK_SIZE);
	if (normalizedError) throw normalizedError;

	// @ts-ignore - untyped rows from stale generated types
	const normalizedIds: string[] = (normalizedMatches || []).map(row => row.normalized_skill_id);
	for (const idChunk of chunk(normalizedIds, IN_CHUNK_SIZE)) {
		// @ts-ignore - table exists but types not yet regenerated
		const { data, error } = await supabase
			.from('candidate_skills')
			.select('candidate_id')
			.eq('organization_uuid', organizationUuid)
			.in('normalized_skill_id', idChunk);
		if (error) throw error;
		// @ts-ignore - untyped rows from stale generated types
		sets.push(new Set((data || []).map(row => row.candidate_id)));
	}

	// Legacy path: exact-element match on candidates.skills text[] with normalization variants
	const variants = Array.from(new Set([
		skill,
		skill.toLowerCase(),
		skill.toUpperCase(),
		skill.charAt(0).toUpperCase() + skill.slice(1).toLowerCase(),
		normalizeSkill(skill),
	]));
	const { data: legacyMatches, error: legacyError } = await supabase
		.from('candidates')
		.select('id')
		.eq('organization_uuid', organizationUuid)
		.overlaps('skills', variants);
	if (legacyError) throw legacyError;
	sets.push(new Set((legacyMatches || []).map(row => row.id)));

	return union(sets);
}

async function resolveSkillIds(skills: PeopleSearchCriteria['skills'], organizationUuid: string): Promise<Set<string>> {
	const perSkill = await Promise.all(skills.names.map(name => resolveSingleSkillIds(name, organizationUuid)));
	if (perSkill.length === 0)
		return new Set();
	if (skills.mode === 'all')
		return perSkill.reduce((acc, set) => intersect(acc, set));
	return union(perSkill);
}

async function resolveLanguageIds(languages: PeopleSearchCriteria['languages'], organizationUuid: string): Promise<Set<string>> {
	const sets: Set<string>[] = [];

	for (const item of languages.items) {
		// @ts-ignore - table exists but types not yet regenerated
		let query = supabase
			.from('candidate_languages')
			.select('candidate_id, candidates!inner(organization_uuid)')
			.eq('candidates.organization_uuid', organizationUuid)
			.eq('language_code', item.code);
		if (item.minCefr !== null)
			query = query.gte('cefr_level', item.minCefr);
		const { data, error } = await query;
		if (error) throw error;
		// @ts-ignore - untyped rows from stale generated types
		sets.push(new Set((data || []).map(row => row.candidate_id)));
	}

	return union(sets);
}

async function resolveListIds(lists: PeopleSearchCriteria['lists']): Promise<Set<string>> {
	const sets: Set<string>[] = [];
	for (const idChunk of chunk(lists.ids, IN_CHUNK_SIZE)) {
		// @ts-ignore - table exists but types not yet regenerated
		const { data, error } = await supabase
			.from('list_members')
			.select('candidate_id')
			.in('list_id', idChunk);
		if (error) throw error;
		// @ts-ignore - untyped rows from stale generated types
		sets.push(new Set((data || []).map(row => row.candidate_id)));
	}
	return union(sets);
}

// One keyword term -> candidate ids with evidence for it.
// Primary: full-text search RPC. Fallback: ilike over key candidate text columns.
async function resolveKeywordTermIds(term: string, organizationUuid: string): Promise<Set<string>> {
	const { data, error } = await supabase.rpc('get_candidates_with_fts', { query: term });
	if (!error && data)
		return new Set(data.map(row => row.id));

	const pattern = `%${sanitizePattern(term)}%`;
	const { data: fallback, error: fallbackError } = await supabase
		.from('candidates')
		.select('id')
		.eq('organization_uuid', organizationUuid)
		.or(`name.ilike.${pattern},function.ilike.${pattern},ai_synthesis.ilike.${pattern},location.ilike.${pattern}`);
	if (fallbackError) throw fallbackError;
	return new Set((fallback || []).map(row => row.id));
}

interface KeywordSets {
	// Sets that every result must belong to (required terms + one per any-of group)
	include: Set<string>[];
	// Candidates matching any excluded term
	exclude: Set<string>;
}

async function resolveKeywordSets(keywords: KeywordCriteria, organizationUuid: string): Promise<KeywordSets> {
	const include: Set<string>[] = [];

	for (const term of keywords.required)
		include.push(await resolveKeywordTermIds(term, organizationUuid));

	for (const group of keywords.anyOfGroups) {
		if (group.length === 0)
			continue;
		const perTerm = await Promise.all(group.map(term => resolveKeywordTermIds(term, organizationUuid)));
		include.push(union(perTerm));
	}

	const excludedSets = await Promise.all(keywords.excluded.map(term => resolveKeywordTermIds(term, organizationUuid)));
	return { include, exclude: union(excludedSets) };
}

// ---------------------------------------------------------------------------
// Direct filters on the candidates table
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyDirectFilters(query: any, criteria: PeopleSearchCriteria): any {
	if (criteria.status.values.length > 0)
		query = query.in('status', criteria.status.values);
	if (criteria.location.values.length > 0)
		query = query.or(criteria.location.values.map(v => `location.ilike.%${sanitizePattern(v)}%`).join(','));
	if (criteria.profile.name.trim() !== '')
		query = query.ilike('name', `%${criteria.profile.name.trim()}%`);
	if (criteria.profile.managerIds.length > 0)
		query = query.in('manager', criteria.profile.managerIds);
	if (criteria.profile.createdFrom)
		query = query.gte('created_at', criteria.profile.createdFrom);
	if (criteria.profile.createdTo)
		query = query.lte('created_at', `${criteria.profile.createdTo}T23:59:59`);
	return query;
}

function hasIdSetCategory(criteria: PeopleSearchCriteria): boolean {
	return isCategoryActive(criteria, 'role')
		|| isCategoryActive(criteria, 'company')
		|| isCategoryActive(criteria, 'skills')
		|| isCategoryActive(criteria, 'languages')
		|| isCategoryActive(criteria, 'lists')
		|| isCategoryActive(criteria, 'keywords');
}

// Fetch the org universe (id + sort fields) matching the direct filters, batched
async function fetchDirectUniverse(criteria: PeopleSearchCriteria, organizationUuid: string): Promise<UniverseRow[]> {
	const rows: UniverseRow[] = [];
	let from = 0;

	for (;;) {
		let query = supabase
			.from('candidates')
			.select('id, created_at, name')
			.eq('organization_uuid', organizationUuid)
			.order('created_at', { ascending: false })
			.order('id', { ascending: true })
			.range(from, from + UNIVERSE_BATCH_SIZE - 1);
		query = applyDirectFilters(query, criteria);

		const { data, error } = await query;
		if (error) throw error;
		rows.push(...(data || []));
		if (!data || data.length < UNIVERSE_BATCH_SIZE)
			break;
		from += UNIVERSE_BATCH_SIZE;
	}

	return rows;
}

// ---------------------------------------------------------------------------
// Public API — one resolution pass feeds both the live count and page fetches
// ---------------------------------------------------------------------------

export async function resolvePeopleSearch(criteria: PeopleSearchCriteria): Promise<ResolvedPeopleSearch> {
	const organizationUuid = await getCurrentOrganization();

	if (!hasIdSetCategory(criteria)) {
		let query = supabase
			.from('candidates')
			.select('id', { count: 'exact', head: true })
			.eq('organization_uuid', organizationUuid);
		query = applyDirectFilters(query, criteria);
		const { count, error } = await query;
		if (error) throw error;
		return { mode: 'direct', count: count || 0 };
	}

	const [universe, roleIds, companyIds, skillIds, languageIds, listIds, keywordSets] = await Promise.all([
		fetchDirectUniverse(criteria, organizationUuid),
		isCategoryActive(criteria, 'role') ? resolveRoleIds(criteria.role, organizationUuid) : null,
		isCategoryActive(criteria, 'company') ? resolveCompanyIds(criteria.company, organizationUuid) : null,
		isCategoryActive(criteria, 'skills') ? resolveSkillIds(criteria.skills, organizationUuid) : null,
		isCategoryActive(criteria, 'languages') ? resolveLanguageIds(criteria.languages, organizationUuid) : null,
		isCategoryActive(criteria, 'lists') ? resolveListIds(criteria.lists) : null,
		isCategoryActive(criteria, 'keywords') ? resolveKeywordSets(criteria.keywords, organizationUuid) : null,
	]);

	const includeSets = [roleIds, companyIds, skillIds, languageIds, listIds]
		.filter((set): set is Set<string> => set !== null);
	if (keywordSets)
		includeSets.push(...keywordSets.include);
	const excludeSet = keywordSets ? keywordSets.exclude : new Set<string>();

	// Universe is already sorted created_at desc — filter it in order
	const rows = universe
		.filter(row => includeSets.every(set => set.has(row.id)) && !excludeSet.has(row.id));

	return { mode: 'idset', count: rows.length, rows };
}

// Comparator over universe rows for client-side idset ordering.
// Name sorts are locale-aware with null names last in both directions.
function compareUniverseRows(sort: PeopleSearchSort) {
	return (a: UniverseRow, b: UniverseRow): number => {
		switch (sort) {
			case 'newest':
				return b.created_at.localeCompare(a.created_at) || a.id.localeCompare(b.id);
			case 'oldest':
				return a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id);
			case 'nameAsc':
			case 'nameDesc': {
				if (a.name === null && b.name === null)
					return a.id.localeCompare(b.id);
				if (a.name === null)
					return 1;
				if (b.name === null)
					return -1;
				const compared = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
				return (sort === 'nameAsc' ? compared : -compared) || a.id.localeCompare(b.id);
			}
		}
	};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applySortOrder(query: any, sort: PeopleSearchSort): any {
	switch (sort) {
		case 'newest':
			return query.order('created_at', { ascending: false }).order('id', { ascending: true });
		case 'oldest':
			return query.order('created_at', { ascending: true }).order('id', { ascending: true });
		case 'nameAsc':
			return query.order('name', { ascending: true, nullsFirst: false }).order('id', { ascending: true });
		case 'nameDesc':
			return query.order('name', { ascending: false, nullsFirst: false }).order('id', { ascending: true });
	}
}

export async function fetchPeopleSearchPage(
	criteria: PeopleSearchCriteria,
	resolved: ResolvedPeopleSearch,
	page: number,
	sort: PeopleSearchSort = DEFAULT_PEOPLE_SEARCH_SORT,
): Promise<{ rows: Candidate[]; total: number }> {
	const organizationUuid = await getCurrentOrganization();
	const from = (page - 1) * PEOPLE_SEARCH_PAGE_SIZE;

	if (resolved.mode === 'direct') {
		let query = supabase
			.from('candidates')
			.select(CANDIDATE_COLUMNS)
			.eq('organization_uuid', organizationUuid);
		query = applySortOrder(query, sort)
			.range(from, from + PEOPLE_SEARCH_PAGE_SIZE - 1);
		query = applyDirectFilters(query, criteria);
		const { data, error } = await query;
		if (error) throw error;
		return { rows: (data || []) as unknown as Candidate[], total: resolved.count };
	}

	const sorted = [...(resolved.rows || [])].sort(compareUniverseRows(sort));
	const pageIds = sorted.slice(from, from + PEOPLE_SEARCH_PAGE_SIZE).map(row => row.id);
	if (pageIds.length === 0)
		return { rows: [], total: resolved.count };

	const { data, error } = await supabase
		.from('candidates')
		.select(CANDIDATE_COLUMNS)
		.in('id', pageIds);
	if (error) throw error;

	// Preserve the requested ordering across the unordered .in() fetch
	const byId = new Map((data || []).map(row => [row.id, row]));
	const rows = pageIds
		.map(id => byId.get(id))
		.filter((row): row is NonNullable<typeof row> => row !== undefined);
	return { rows: rows as unknown as Candidate[], total: resolved.count };
}
